import axios, {
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { Configuration } from "../../services/OpenAPI";

// .env の設定がない場合はデフォルト値を使用
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

// axiosインスタンスを作成
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

// 認証トークン（JWT）を Authorization ヘッダに付与する。
// フロント(rakulogi.com)とAPI(autofba.net)が別ドメイン=cross-siteのためCookieではなくヘッダで送る。
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    // axios v1 の AxiosHeaders には必ず set() で入れる（直接代入は送信されない）
    config.headers = AxiosHeaders.from(config.headers ?? {});
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

// 「本物の認証失敗（トークン無し/無効/失効）」を検知したら認証状態をクリアしてログインへ誘導する。
//
// 注意: バックエンドの各ハンドラはDB・業務エラーも 401 で返してしまう箇所が多数ある
// （例: プラン詳細取得の失敗）。そのため単純に status===401 でログアウトすると、
// 詳細を開いただけでログイン画面に戻される。認証ミドルウェアが付与する
// code==="UNAUTHENTICATED" が付いた応答のみをログアウト対象とする。
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthFailure =
      error?.response?.status === 401 &&
      error?.response?.data?.code === "UNAUTHENTICATED";
    if (isAuthFailure) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem("loginHistoryId");
      // ログイン画面での認証失敗（誤ったパスワード等）で無限リダイレクトしないようガード
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// axiosをfetchのインターフェースにラップ
export const axiosFetch = async (
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<any> => {
  let url: string;

  if (!input) {
    console.error("axiosFetch: input is undefined");
    throw new Error("Invalid URL: input is undefined");
  }

  if (input instanceof URL) {
    url = input.href;
  } else {
    url = input.toString();
  }

  if (
    url.startsWith("http://localhost") ||
    url.startsWith("https://localhost")
  ) {
    url = url.replace(/http[s]?:\/\/localhost(:\d+)?/, API_BASE_URL);
  }

  const authToken = localStorage.getItem("authToken");
  const axiosConfig: AxiosRequestConfig = {
    url,
    method: init?.method || "GET",
    headers: new AxiosHeaders({
      ...(init?.headers as any),
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    }),
    data: init?.body,
    withCredentials: true,
  };
  try {
    const response: AxiosResponse = await apiClient(axiosConfig);
    return {
      ok: response.status >= 200 && response.status < 300,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      json: async () => response.data,
      text: async () => JSON.stringify(response.data),
    };
  } catch (error: any) {
    console.error("Axios fetch error:", error);
    // AxiosError かつ response がある場合
    if (error?.response) {
      throw {
        status: error.response.status,
        message: error.message,
        response: error.response,
      };
    }
    // それ以外（Network Errorなど）
    throw {
      status: 0,
      message: error.message || "Network Error",
    };
  }
};

// Configurationの作成
export const createConfiguration = () => {
  return new Configuration({
    fetchApi: axiosFetch,
  });
};
